import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import WarningPage from './WarningPage';
import RetryNotice, { RetryInline } from './RetryNotice';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import { RULES, numberField, validateNumber } from './thresholdValidation';
import MetricDefinitionNote from './MetricDefinitionNote';
import SetBy, { lastSaver } from './SetBy';

// Marketing Expense input for LCRR (Requirement Document v1.0 S4.8/S7) -
// "Finance must enter marketing expense because it is not currently
// available in DB." Reuses the same granular Lead Category list already
// populated for LCR (no separate category set - confirmed with the
// user). One editable amount per category, per month - saving upserts
// (create-or-correct), not versioned like a threshold rule, since this is
// a Finance-entered FACT about what was actually spent, not a rule that
// must be protected from retroactive change.
const MarketingExpenseConfig = () => {
  const [month, setMonth] = useState(dayjs());
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [expenseByCategory, setExpenseByCategory] = useState({});
  const [savedBy, setSavedBy] = useState({}); // category -> the saved row (who entered it, and when) - kept apart from the boxes being typed in
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingCategory, setSavingCategory] = useState(null);
  // categories whose Save was clicked - a blank amount only complains after that
  const [attempted, setAttempted] = useState({});

  // The category list's own load - a failure used to leave the table silently empty. Retry reloads only this list.
  const loadCategories = () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    return apiRequest({ url: 'warning-dashboard/lead-categories', method: 'get' })
      .then((res) => {
        if (res?.status !== 'success') {
          setCategoriesError(res?.message || 'Could not load the lead categories');
          return;
        }
        setCategories(res?.data?.categories || []);
      })
      .catch((err) => setCategoriesError(err?.response?.data?.message || 'Could not load the lead categories'))
      .finally(() => setCategoriesLoading(false));
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const fetchExpenses = () => {
    setLoading(true);
    // apiRequest resolves even on a failed call (src/api/request.js) - this status check is required, a
    // .catch alone would never fire and a server error would silently render as "nothing entered yet".
    return apiRequest({
      url: `warning-dashboard/marketing-expense?year=${month.year()}&month=${month.month() + 1}`,
      method: 'get',
    })
      .then((res) => {
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load marketing expense');
          return;
        }
        const map = {};
        (res?.data?.rows || []).forEach((r) => { map[r.lead_category] = r.expense_amount; });
        setExpenseByCategory(map);
        setSavedBy(Object.fromEntries((res?.data?.rows || []).map((r) => [r.lead_category, r])));
        setError(null);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Could not load marketing expense'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // After a save only "who saved it" is refreshed: the amounts still being typed into other rows must not be overwritten.
  const refreshSavedBy = () =>
    apiRequest({ url: `warning-dashboard/marketing-expense?year=${month.year()}&month=${month.month() + 1}`, method: 'get' })
      .then((res) => {
        if (res?.status === 'success') setSavedBy(Object.fromEntries((res?.data?.rows || []).map((r) => [r.lead_category, r])));
      })
      .catch(() => {}); // the amount itself is saved; the "Set by" caption just stays as it was

  const handleChange = (category, value) => {
    setExpenseByCategory((prev) => ({ ...prev, [category]: value }));
  };

  const handleSave = (category) => {
    // BUG-03: a blank amount used to be saved as 0 without a word. Enter 0 if nothing was spent.
    setAttempted((prev) => ({ ...prev, [category]: true }));
    if (validateNumber(expenseByCategory[category], 'Expense amount', RULES.nonNegative14)) return;
    setSavingCategory(category);
    apiRequest({
      url: 'warning-dashboard/marketing-expense',
      method: 'post',
      data: {
        year: month.year(),
        month: month.month() + 1,
        lead_category: category,
        expense_amount: Number(expenseByCategory[category]),
      },
    })
      .then((res) => {
        if (res?.status !== 'success') {
          setError(res?.message || `Could not save the amount for ${category}`);
          return;
        }
        refreshSavedBy();
      })
      .catch(() => setError(`Could not save the amount for ${category}`))
      .finally(() => setSavingCategory(null));
  };

  return (
    <WarningPage
      title="LCRR - Marketing Expense"
      actions={
        <Button component={Link} to="/dashboard/warning-dashboard/lcrr" variant="outlined">
          View LCRR Results
        </Button>
      }
    >
      <Box sx={{ mb: 2 }}>
        <MetricDefinitionNote metricKey="lcrr" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter the actual marketing spend for each lead category, for the
        selected month. A category left blank costs 0 per lead (this is
        also how a free channel like Referral naturally shows as 0 cost,
        with no special case needed).
      </Typography>

      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
        {(loading || categoriesLoading) && <CircularProgress size={24} />}
      </Box>

      {!loading && error && <RetryNotice message={error} onRetry={fetchExpenses} />}
      {!categoriesLoading && categoriesError && <RetryNotice message={categoriesError} onRetry={loadCategories} />}

      {!loading && !categoriesLoading && categories.length === 0 && (
        <Typography color="text.secondary">No lead categories available.</Typography>
      )}

      {!loading && !categoriesLoading && categories.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                <TableCell><strong>Lead Category</strong></TableCell>
                <TableCell align="right"><strong>Marketing Expense</strong></TableCell>
                <TableCell><strong>Set by</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category} hover>
                  <TableCell>{category}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={expenseByCategory[category] ?? ''}
                      onChange={(e) => handleChange(category, e.target.value)}
                      sx={{ width: 160 }}
                      {...numberField(expenseByCategory[category] ?? '', 'Expense amount', RULES.nonNegative14, !!attempted[category]).props}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell>
                    {/* an amount is corrected in place, so this is the last person who saved it (not the first) */}
                    <SetBy {...lastSaver(savedBy[category])} />
                  </TableCell>
                  <TableCell>
                    <UpdateButton
                      size="small"
                      action={() => handleSave(category)}
                      disabled={savingCategory === category}
                    >
                      {savingCategory === category ? 'Saving...' : 'Save'}
                    </UpdateButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </WarningPage>
  );
};

export default MarketingExpenseConfig;
